import React, { memo } from "react";

import { Descriptions, Modal, Select, Spin } from "antd";
import { CgCreditCard } from "react-icons/cg";

import { ConfirmationModal } from "../../../ConfirmationModal";
import useChangeClientProductModal from "../../../../hooks/useChangeClientProduct/useChangeClientProductModal";
import { ChangeClientProductModalProps } from "../../../../hooks/useChangeClientProduct/types";
import * as S from "./styles";

const { Option } = Select;

const ChangeClientProductModal: React.FC<ChangeClientProductModalProps> = ({
    open,
    onClose,
    onSuccess,
}) => {
    const {
        currentProductName,
        isCurrentProductPhysical,
        stores,
        products,
        selectedStoreId,
        setSelectedStoreId,
        selectedProductId,
        setSelectedProductId,
        loadingStores,
        loadingProducts,
        submitting,
        isStoreSelectionDisabled,
        isConfirmationOpen,
        confirmationDescription,
        handleClose,
        handleCloseConfirmation,
        handleRequestConfirm,
        handleConfirmChange,
        canConfirm,
    } = useChangeClientProductModal({ open, onClose, onSuccess });

    const isFormLoading = loadingStores || loadingProducts;

    return (
        <>
            <Modal
                title="Alteração de Produto"
                open={open}
                onCancel={handleClose}
                footer={null}
                destroyOnClose
                maskClosable={!isFormLoading && !submitting && !isConfirmationOpen}
                closable={!isFormLoading && !submitting && !isConfirmationOpen}
                width={560}
                data-testid="modal-change-client-product"
            >
                <Spin spinning={isFormLoading} data-testid="spin-change-client-product">
                    <div data-testid="content-change-client-product">
                        <S.FormField data-testid="field-product-current">
                            <S.FieldLabel>Produto atual</S.FieldLabel>
                            <S.ReadOnlyField data-testid="readonly-current-product">
                                <CgCreditCard size={16} />
                                <span data-testid="text-modal-current-product-name">
                                    {currentProductName}
                                </span>
                            </S.ReadOnlyField>
                        </S.FormField>

                        <S.FormField data-testid="field-store">
                            <S.FieldLabel>Loja</S.FieldLabel>
                            <Select
                                data-testid="select-change-product-store"
                                value={selectedStoreId}
                                onChange={(value) => setSelectedStoreId(value)}
                                style={{ width: "100%" }}
                                placeholder={
                                    loadingStores ? "Carregando lojas..." : "Selecione uma loja"
                                }
                                loading={loadingStores}
                                disabled={isStoreSelectionDisabled}
                                showSearch
                                optionFilterProp="name"
                                optionLabelProp="name"
                            >
                                {stores.map((store) => (
                                    <Option
                                        key={store.id}
                                        value={store.id}
                                        name={`${store.nomeFantasia ?? ""} ${store.razaoSocial ?? ""}`}
                                        data-testid={`option-store-${store.id}`}
                                    >
                                        <Descriptions column={1} size="small" layout="horizontal">
                                            {store.nomeFantasia && (
                                                <Descriptions.Item
                                                    label="Nome Fantasia"
                                                    labelStyle={{ fontWeight: 700 }}
                                                >
                                                    {store.nomeFantasia}
                                                </Descriptions.Item>
                                            )}
                                            <Descriptions.Item
                                                label="Razão Social"
                                                labelStyle={{ fontWeight: 700 }}
                                            >
                                                {store.razaoSocial}
                                            </Descriptions.Item>
                                        </Descriptions>
                                    </Option>
                                ))}
                            </Select>
                        </S.FormField>

                        <S.FormField data-testid="field-new-product">
                            <S.FieldLabel>Novo produto</S.FieldLabel>
                            <Select
                                data-testid="select-change-product-new"
                                value={selectedProductId}
                                onChange={(value) => setSelectedProductId(value)}
                                style={{ width: "100%" }}
                                placeholder={
                                    !isCurrentProductPhysical
                                        ? "Troca disponível apenas para produto físico"
                                        : loadingProducts
                                            ? "Carregando produtos..."
                                            : "Selecione o novo produto"
                                }
                                loading={loadingProducts}
                                disabled={
                                    !isCurrentProductPhysical ||
                                    loadingProducts ||
                                    products.length === 0
                                }
                                showSearch
                                optionFilterProp="children"
                            >
                                {products.map((product) => (
                                    <Option
                                        key={product.id}
                                        value={product.id}
                                        data-testid={`option-product-${product.id}`}
                                    >
                                        {product.descricao}
                                    </Option>
                                ))}
                            </Select>
                        </S.FormField>
                    </div>
                </Spin>

                <S.FooterActions data-testid="footer-change-client-product">
                    <S.FooterCancelButton
                        type="button"
                        onClick={handleClose}
                        disabled={isFormLoading || submitting}
                        data-testid="btn-cancel-change-client-product"
                    >
                        Cancelar
                    </S.FooterCancelButton>
                    <S.FooterConfirmButton
                        type="button"
                        onClick={handleRequestConfirm}
                        disabled={!canConfirm}
                        data-testid="btn-confirm-change-client-product"
                    >
                        Confirmar
                    </S.FooterConfirmButton>
                </S.FooterActions>
            </Modal>

            {isConfirmationOpen && (
                <div data-testid="modal-confirm-change-client-product">
                    <ConfirmationModal
                        title="Confirmar alteração de produto"
                        description={confirmationDescription}
                        isLoading={submitting}
                        onClose={handleCloseConfirmation}
                        onSubmit={handleConfirmChange}
                        submitText="Confirmar"
                        cancelText="Cancelar"
                        cancelDataTestId="btn-cancel-change-product-action"
                        submitDataTestId="btn-confirm-change-product-action"
                    />
                </div>
            )}
        </>
    );
};

export default memo(ChangeClientProductModal);